"use client";

/**
 * ConfirmPopover
 *
 * Requirements Summary:
 * - Lightweight inline confirmation before destructive actions (delete, discard, etc.)
 * - Renders a small popover with a message, cancel, and confirm button
 * - Click-outside or cancel dismisses without action
 * - Reusable across the app wherever a modal feels too heavy
 * - Can stop click propagation when used inside larger clickable rows
 *
 * Related files:
 * - /components/ui/popover/Popover.tsx — base popover (this is a purpose-built variant)
 * - /components/ui/button/Button.tsx — shared button component
 */

import React, { useState, useRef, useEffect } from "react";
import Button from "@/components/ui/button/Button";

type Position = "top" | "bottom";

interface ConfirmPopoverProps {
  /** The element that triggers the popover on click */
  trigger: React.ReactNode;
  /** Short confirmation message shown in the popover */
  message: string;
  /** Label for the confirm button (default: "Delete") */
  confirmLabel?: string;
  /** Button variant for the confirm action (default: "danger") */
  confirmVariant?: "danger" | "warning" | "primary";
  /** Called when the user confirms */
  onConfirm: () => void | Promise<void>;
  /** Popover position relative to the trigger (default: "bottom") */
  position?: Position;
  /** Prevent clicks in the popover from bubbling to parent clickable rows */
  stopPropagation?: boolean;
}

export default function ConfirmPopover({
  trigger,
  message,
  confirmLabel = "Delete",
  confirmVariant = "danger",
  onConfirm,
  position = "bottom",
  stopPropagation = false,
}: ConfirmPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
    } finally {
      setIsConfirming(false);
      setIsOpen(false);
    }
  };

  const positionClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
  };

  const arrowClasses = {
    top: "bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rotate-45",
    bottom: "top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-45",
  };

  return (
    <div
      className="relative inline-block"
      onClick={(event) => {
        if (stopPropagation) event.stopPropagation();
      }}
    >
      <div ref={triggerRef} onClick={() => setIsOpen(!isOpen)}>
        {trigger}
      </div>
      {isOpen && (
        <div
          ref={popoverRef}
          className={`absolute z-99999 w-56 ${positionClasses[position]}`}
        >
          <div className="rounded-xl bg-white p-3 shadow-theme-lg dark:bg-[#1E2634]">
            <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">
              {message}
            </p>
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsOpen(false)}
                disabled={isConfirming}
              >
                Cancel
              </Button>
              <Button
                variant={confirmVariant}
                size="sm"
                onClick={handleConfirm}
                disabled={isConfirming}
              >
                {isConfirming ? "..." : confirmLabel}
              </Button>
            </div>
            <div
              className={`absolute h-3 w-3 bg-white shadow-theme-lg dark:bg-[#1E2634] ${arrowClasses[position]}`}
            />
          </div>
        </div>
      )}
    </div>
  );
}
